import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  activeCutIndexAtTime,
  cutPreviewUrl,
  nextPreviewAtTime,
  previewClipKey,
} from '../utils/newNle';

function loadUrl(el, url, localTime) {
  if (!el || !url) return Promise.resolve(false);
  const same = el.getAttribute('data-cut-url') === url;
  return new Promise((resolve) => {
    let settled = false;
    let timer = null;
    const finish = (ok) => {
      if (settled) return;
      settled = true;
      if (timer) clearTimeout(timer);
      el.removeEventListener('canplay', onReady);
      el.removeEventListener('error', onErr);
      resolve(ok);
    };
    const onReady = () => {
      try {
        if (Math.abs((el.currentTime || 0) - localTime) > 0.12) el.currentTime = localTime;
      } catch (_) {}
      finish(true);
    };
    const onErr = () => finish(false);
    el.addEventListener('canplay', onReady);
    el.addEventListener('error', onErr);
    if (same && el.readyState >= 2) {
      onReady();
      return;
    }
    if (!same) {
      el.src = url;
      el.setAttribute('data-cut-url', url);
    }
    try { el.currentTime = localTime; } catch (_) {}
    if (el.readyState >= 2) onReady();
    if (!settled) timer = setTimeout(() => finish(el.readyState >= 2), 1800);
  });
}

/**
 * A/B proxy players for when native HLS is unavailable.
 * During play the visible decoder is never reloaded — the back buffer prerolls
 * the next cut. Scrubbing never seeks; parent shows a poster.
 */
export default function NleProgressivePreview({
  cuts,
  playhead,
  playing,
  scrubbing = false,
  fastPacing,
  flipbookIntro,
  flashPool,
  className,
  activeSlot = null,
}) {
  const aRef = useRef(null);
  const bRef = useRef(null);
  const [front, setFront] = useState(0);
  const [ready, setReady] = useState(false);
  const frontRef = useRef(0);
  const loadedKeyRef = useRef(['', '']);
  const seekGenRef = useRef(0);
  const playingRef = useRef(!!playing);

  playingRef.current = !!playing;
  frontRef.current = front;

  // Parent already resolved the slot for this playhead; reuse it instead of recomputing per tick.
  const slot = useMemo(
    () => activeSlot || activeCutIndexAtTime(cuts, playhead, fastPacing, flipbookIntro, flashPool),
    [activeSlot, cuts, playhead, fastPacing, flipbookIntro, flashPool]
  );
  const slotKey = previewClipKey(slot);
  const slotUrl = cutPreviewUrl(slot?.cut);
  const slotLocal = slot?.kind === 'intro' ? 0 : (slot?.localTime || 0);

  const pair = () => [aRef.current, bRef.current];

  useEffect(() => {
    if (scrubbing) {
      pair().forEach((el) => { try { el?.pause(); } catch (_) {} });
      return undefined;
    }
    if (!slotUrl || !slotKey) {
      pair().forEach((el) => { try { el?.pause(); } catch (_) {} });
      return undefined;
    }

    const gen = ++seekGenRef.current;
    const f = frontRef.current;
    const back = 1 - f;
    const els = pair();
    const frontEl = els[f];
    const backEl = els[back];
    let cancelled = false;

    (async () => {
      if (loadedKeyRef.current[f] === slotKey && frontEl) {
        setReady(true);
        if (playing) {
          if (frontEl.paused) frontEl.play().catch(() => {});
        } else {
          frontEl.pause();
          try {
            if (Math.abs((frontEl.currentTime || 0) - slotLocal) > 0.18) {
              frontEl.currentTime = slotLocal;
            }
          } catch (_) {}
        }
      } else {
        setReady(false);
        const ok = await loadUrl(backEl, slotUrl, slotLocal);
        if (cancelled || seekGenRef.current !== gen || !ok) return;
        try { frontEl?.pause(); } catch (_) {}
        if (playingRef.current) backEl?.play().catch(() => {});
        else backEl?.pause();
        frontRef.current = back;
        setFront(back);
        setReady(true);
        loadedKeyRef.current[back] = slotKey;
        loadedKeyRef.current[f] = '';
      }

      const next = nextPreviewAtTime(cuts, playhead, fastPacing, flipbookIntro, flashPool);
      const nextUrl = cutPreviewUrl(next?.cut);
      const idle = pair()[1 - frontRef.current];
      if (nextUrl && idle && idle.getAttribute('data-cut-url') !== nextUrl) {
        idle.preload = 'auto';
        idle.src = nextUrl;
        idle.setAttribute('data-cut-url', nextUrl);
        try { idle.currentTime = 0; } catch (_) {}
        loadedKeyRef.current[1 - frontRef.current] = previewClipKey(next);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slotKey, slotUrl, playing, scrubbing, cuts, fastPacing, flipbookIntro, flashPool]);

  useEffect(() => {
    if (playing || scrubbing || !slotUrl) return;
    const el = pair()[frontRef.current];
    if (!el || loadedKeyRef.current[frontRef.current] !== slotKey) return;
    try {
      if (Math.abs((el.currentTime || 0) - slotLocal) > 0.18) el.currentTime = slotLocal;
    } catch (_) {}
  }, [playing, scrubbing, slotKey, slotUrl, slotLocal]);

  useEffect(() => () => {
    pair().forEach((el) => {
      try { el?.pause(); el?.removeAttribute('src'); el?.load(); } catch (_) {}
    });
  }, []);

  const dim = typeof className === 'string' && className.includes('--flash');
  const vis = (isFront) => {
    if (scrubbing || !ready || front !== isFront) return 0;
    return dim ? 0.72 : 1;
  };

  return (
    <>
      <video
        ref={aRef}
        className={className}
        muted
        playsInline
        preload="auto"
        style={{ opacity: vis(0), zIndex: 1 }}
      />
      <video
        ref={bRef}
        className={className}
        muted
        playsInline
        preload="auto"
        style={{ opacity: vis(1), zIndex: 1 }}
      />
    </>
  );
}
